import { Injectable } from '@angular/core';
import { ApiHttpClient } from 'app/http/clients/api';
import { Deal } from 'app/models/dto/deal.model';
import { Document } from 'app/models/dto/document.model';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DealApplicationRequirement } from './deal-application-requirement.model';
import { DocumentRequirementRequest, DocumentRequirements, RemainingDocumentRequirement } from 'app/models/document-requirement/document-requirement.model';
import { HttpResponse } from '@angular/common/http';
@Injectable({
  providedIn: 'root'
})
export class DealDocumentsService {
  protected readonly previewableMimeTypes = [
    'image/bmp',
    'text/csv',
    'image/gif',
    'image/jpeg',
    'application/pdf',
    'image/png',
    'text/tab-separated-values',
    'text/plain',
    'audio/wav',
    'audio/x-wav',
    'application/json'
  ];

  constructor(
    private http: ApiHttpClient
  ) {}

  // gets all deal document requirements
  getDocumentRequirements(loanProductId: number): Observable<DealApplicationRequirement[]> {
    return this.http.get<DealApplicationRequirement[]>(`${environment.API_URL}/matching/loan-product/${loanProductId}/documents`);
  }

  attachSignedApplication(dealId: number, signedApplicationDocId: number): Observable<Deal> {
    return this.http.put<Deal>(`${environment.API_URL}/deal/${dealId}`, { signedApplicationDocId });
  }

  detachSignedApplication(dealId: number): Observable<Deal> {
    return this.http.put<Deal>(`${environment.API_URL}/deal/${dealId}`, { signedApplicationDocId: null });
  }

  uploadDealDocument(file: File, borrowerId: number, dealId: number, category: string): Observable<Document> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('dealId', `${dealId}`);
    formData.append('category', category);
    formData.append('borrowerId', `${borrowerId}`);
    formData.append('attach', 'true');
    return this.http.post<Document>(`${environment.API_URL}/document`, formData).pipe(
      map((document: any) => {
        document.lenderUser = document.lender_user;
        return document;
      })
    );
  }

  // Advanced Deal Documents
  // Create/upload a new document.
  postAdvancedDealDocument(file: File, formData: FormData): Observable<Document> {
    // remove all backslahes in a file name
    const fileNameSanitized = file && file.name && file.name.replace(/\\/g, '');
    // create a new instance of a file to pass with the name sanitized ( stripped slahes) since file is immutable
    const newFile = new File([file], fileNameSanitized);
    formData.append('file', fileNameSanitized ? newFile : file);
    formData.append('uploadSource', 'pipeline');

    const dealId = formData.get('dealId');
    const postUrl = `${environment.API_URL}/deals/${dealId}/documents`;

    return this.http.post<Document>(postUrl, formData);
  }

  attachAdvancedDealDocument(dealId: number, documentId: number): Observable<Document> {
    return this.http.post<Document>(`${environment.API_URL}/deals/${dealId}/documents/${documentId}`, null);
  }

  patchDealDocument(dealId: number, documentUpdates: Partial<Document>) {
    return this.http.patch<Document>(
      `${environment.API_URL}/deals/${dealId}/documents/${documentUpdates.id}/metadata`,
      documentUpdates
    );
  }

  getAdvancedDealDocuments(dealId: number): Observable<Document[]> {
    return this.http.get<Document[]>(`${environment.API_URL}/deals/${dealId}/documents`);
  }

  reviewDealDocument(dealId: number, documentId: number): Observable<Document> {
    return this.http.patch<Document>(`${environment.API_URL}/deals/${dealId}/documents/${documentId}/review`, null);
  }

  rejectDealDocument(dealId: number, documentId: number, reason: string): Observable<Document> {
    return this.http.patch<Document>(`${environment.API_URL}/deals/${dealId}/documents/${documentId}/reject`, { reason });
  }

  clearDealDocumentStatus(dealId: number, documentId: number): Observable<Document> {
    return this.http.patch<Document>(`${environment.API_URL}/deals/${dealId}/documents/${documentId}/clear-status`, null);
  }

  openInNewTab(url: string, document: Document): void {
    if (document.scanStatus === 'CLEAN') {
      window.open(url, '_blank');
    }
  }

  getAdvancedDocumentRequirements(dealId: number): Observable<{ documentRequirements: DocumentRequirements }> {
    return this.http.get<{ documentRequirements: DocumentRequirements }>(`${environment.API_URL}/deals/${dealId}/document-requirements`);
  }

  postDocumentRequirement(dealId: number, documentRequirements: DocumentRequirementRequest) {
    return this.http.post<DocumentRequirementRequest>(`${environment.API_URL}/deals/${dealId}/document-requirements`, documentRequirements);
  }

  deleteDocumentRequirement(dealId: number, documentRequirement: RemainingDocumentRequirement) {
    const url = `${environment.API_URL}/deals/${dealId}/document-requirements/${documentRequirement.sourceId}`;

    return this.http.delete<DocumentRequirementRequest>(url);
  }

  // Header-aware download: full response for filename/content-type
  downloadDocumentResponse(documentId: number): Observable<HttpResponse<ArrayBuffer>> {
    const url = `${environment.API_URL}/document/${documentId}?stream=1`;
    return this.http.get(url, { responseType: 'arraybuffer', observe: 'response' });
  }

  // Simple Blob download (used for bulk ZIP path)
  downloadDocument(documentId: number): Observable<Blob> {
    const url = `${environment.API_URL}/document/${documentId}?stream=1`;
    return this.http
      .get(url, { responseType: 'arraybuffer', observe: 'response' })
      .pipe(
        map((response: HttpResponse<ArrayBuffer>) => {
          const contentType = response.headers.get('content-type') || 'application/octet-stream';
          return new Blob([response.body as ArrayBuffer], { type: contentType });
        })
      );
  }
}
